import { DateTime } from 'luxon'

/**
 * Grid `valueGetter` for a unix-seconds column. Backend `createdAt` / `updatedAt` are unix
 * SECONDS.
 *
 * Deliberately typed as a plain single-parameter function rather than MUI's
 * `GridValueGetter`: that alias defaults its row generic to `GridValidRowModel`, and
 * `GridValueGetter` is NOT assignable to `GridValueGetter<Customer>`, so annotating it
 * breaks `Array<GridColDef<Customer>>`. A one-param function is assignable to v8's
 * `(value, row, column, apiRef)` shape and typechecks cleanly.
 */
export const parseUnixTimestamp = (value: unknown): Date | null => {
  if (value === null || value === undefined) {
    return null
  }

  const seconds = Number(value)

  if (!Number.isFinite(seconds)) {
    return null
  }

  return new Date(seconds * 1000)
}

/**
 * Grid `valueFormatter`. Pair with `parseUnixTimestamp`, which has already produced a
 * `Date` — do not apply unix-seconds conversion again here. Plain-function typing for the
 * same reason as above.
 */
export const formatDateOnly = (value: unknown): string => {
  if (!value) {
    return '—'
  }

  return DateTime.fromJSDate(value as Date).toFormat('MMM d, yyyy')
}

/**
 * Grid `valueFormatter` that carries the time of day. Pair with `parseUnixTimestamp`, which has
 * already produced a `Date` — do not apply unix-seconds conversion again here. Plain-function
 * typing for the same reason as `formatDateOnly` above; it is also called directly, with a
 * `Date | null`, which `unknown` accepts.
 */
export const formatDateTime = (value: unknown): string => {
  if (!value) {
    return '—'
  }

  return DateTime.fromJSDate(value as Date).toFormat('MMM d, yyyy h:mm a')
}

/**
 * Grid `valueGetter` for an ISO `timestamptz` column — partner-invoice dates arrive as
 * strings, unlike the unix-seconds `createdAt`/`updatedAt` every `BaseEntity`-backed slice in
 * this app returns. Pair with `formatDateOnly`.
 *
 * NOT interchangeable with `parseUnixTimestamp`: feeding an ISO string to that one yields
 * `NaN`, and feeding unix seconds to this one yields `null` (a bare number is rejected here
 * precisely so that mix-up surfaces as an empty cell rather than a 1970 date).
 *
 * Plain single-parameter typing, for the same `GridValueGetter` reason documented above.
 */
export const parseIsoDate = (value: unknown): Date | null => {
  if (typeof value !== 'string' || value === '') {
    return null
  }

  // `setZone: true` keeps the offset the backend sent instead of rebasing to the browser's
  // zone, and the local-midnight Date below pins the calendar date so `formatDateOnly` cannot
  // shift it. Without this, a `…T00:00:00.000Z` due date renders as the PREVIOUS day
  // anywhere west of UTC — verified: "Aug 20, 2026" for 2026-08-21T00:00:00.000Z in
  // America/Los_Angeles.
  const parsed = DateTime.fromISO(value, { setZone: true })

  if (!parsed.isValid) {
    return null
  }

  return new Date(parsed.year, parsed.month - 1, parsed.day)
}

/**
 * Grid `valueGetter` for a timestamp whose wire shape is not fully pinned down. Pair with
 * `formatDateTime`, which carries the time of day.
 *
 * Needed because `GigsafeGetRowUserDto.disabledAt` is typed `string` on the DTO but is produced
 * by `getRawMany()` on a `timestamp` column, so the exact serialization is unverified — see the
 * ENG-480 design doc §9.2. Unparseable input yields `null`, which `formatDateTime` renders as an
 * em dash rather than "Invalid Date".
 *
 * NOT a replacement for `parseIsoDate`: that one deliberately flattens its result to LOCAL
 * MIDNIGHT so a date-only column cannot drift across a timezone, which would erase the time this
 * column has to show.
 */
export const parseFlexibleDateTime = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    // Unix seconds and milliseconds are told apart by magnitude: 1e12 ms is 2001, and 1e12
    // seconds is the year 33658 — no real timestamp is ambiguous.
    const milliseconds = Math.abs(value) < 1e12 ? value * 1000 : value

    return new Date(milliseconds)
  }

  if (typeof value !== 'string') {
    return null
  }

  const parsed = DateTime.fromISO(value, { setZone: true })

  if (!parsed.isValid) {
    return null
  }

  return parsed.toJSDate()
}
